import { IconButton, Radio, Stack } from '@mui/material';
import {
  BooleanOperator,
  CustomField,
  CustomFieldApplicableTo,
  CustomFieldBooleanFilter,
  CustomFieldDataType,
  CustomFieldDateFilter,
  CustomFieldFilter,
  CustomFieldSelectListFilter,
  CustomFieldSelectOption,
  CustomFieldTextNumberFilter,
  DateOperator,
  DEFAULT_DATE_FORMAT_FNS,
  SelectListOperator,
  TextOperator,
  useGetCustomFieldsForSchoolQuery,
} from '@schooly/api';
import { FormTextField } from '@schooly/components/form-text-field';
import { DeleteIcon, FormRadioStyled, PlusIcon, SimpleButton, Skeleton } from '@schooly/style';
import { format } from 'date-fns';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  DateRangeSelect,
  DateSelect,
  DropdownSelectFilter,
  SimpleDropdownSelect,
  toggleArrayProperty,
} from '../';

const TEXT_OPERATOR_OPTIONS = [
  { nameTextId: 'filter-contains', value: TextOperator.Contains },
  { nameTextId: 'filter-None', value: TextOperator.None },
  { nameTextId: 'filter-All', value: TextOperator.All },
];

const BOOLEAN_OPERATOR_OPTIONS = [
  { nameTextId: 'filter-isEqualTo', value: BooleanOperator.IsEqual },
  { nameTextId: 'filter-All', value: BooleanOperator.All },
  { nameTextId: 'filter-None', value: BooleanOperator.None },
];

const SELECT_LIST_OPERATOR_OPTIONS = [
  { nameTextId: 'filter-isOneOf', value: SelectListOperator.IsOneOf },
  { nameTextId: 'filter-All', value: SelectListOperator.All },
  { nameTextId: 'filter-None', value: SelectListOperator.None },
];

const DATE_OPERATOR_OPTIONS = [
  { nameTextId: 'filter-inRange', value: DateOperator.InRange },
  { nameTextId: 'filter-isEqualTo', value: DateOperator.IsEqual },
  { nameTextId: 'filter-All', value: DateOperator.All },
  { nameTextId: 'filter-None', value: DateOperator.None },
];

type OperatorSelectProps<T> = {
  operator: T;
  onUpdate: (operator: T) => void;
  options: { nameTextId: string; value: T }[];
};

const OperatorSelect = <T extends string>({
  operator,
  onUpdate,
  options,
}: OperatorSelectProps<T>) => {
  const { formatMessage } = useIntl();

  return (
    <SimpleDropdownSelect
      options={options}
      value={options.find((option) => option.value === operator)?.value}
      onChange={(v) => {
        const value = options.find((option) => option.value === v)?.value;
        if (!value) return;
        onUpdate(value);
      }}
      placeholder={formatMessage({ id: 'filter-show' })}
    />
  );
};

const CustomFieldSelect: FC<{
  selectedCustomFieldId: string;
  options: CustomField[];
  onChange: (customField: CustomField) => void;
}> = ({ selectedCustomFieldId, options, onChange }) => {
  const { formatMessage } = useIntl();
  const handleChange = (customFieldId: string) => {
    const customField = options.find((option) => option.id === customFieldId);
    if (!customField || customField.id === selectedCustomFieldId) return;
    onChange(customField);
  };

  return (
    <SimpleDropdownSelect
      placeholder={formatMessage({ id: 'filter-filterBy' })}
      value={options.find((option) => option.id === selectedCustomFieldId)?.id}
      options={options.map((option) => ({ name: option.label, value: option.id }))}
      onChange={handleChange}
    />
  );
};

type CustomFieldsFiltersProps = {
  schoolId: string;
  filters?: CustomFieldFilter[];
  onUpdate: (customFields: CustomFieldFilter[]) => void;
  userRole: CustomFieldApplicableTo.Student | CustomFieldApplicableTo.Parent;
};

export const CustomFieldsFilters: FC<CustomFieldsFiltersProps> = ({
  schoolId,
  filters = [],
  onUpdate,
  userRole,
}) => {
  const { data: customFields } = useGetCustomFieldsForSchoolQuery(
    { schoolId },
    { refetchOnMount: 'always' },
  );
  const [isAddingFilter, setIsAddingFilter] = useState(false);
  const { formatMessage } = useIntl();
  const customFieldsAvailable = useMemo(() => {
    if (!customFields) return [];

    const addedFiltersIds = filters.map(({ id }) => id);

    return customFields.filter(
      (c) => c.applicable_to.includes(userRole) && !c.archived && !addedFiltersIds.includes(c.id),
    );
  }, [customFields, filters, userRole]);

  const customFieldToAdd: CustomField | undefined = customFieldsAvailable[0];

  const onAddCustomField = useCallback(
    (cf: CustomField) => {
      setIsAddingFilter(false);
      onUpdate([...filters, createCustomFieldFilter(cf)]);
    },
    [filters, onUpdate],
  );

  const handleSelectFilterToAdd = () => {
    if (!customFieldToAdd) return;
    if (customFieldsAvailable.length === 1) {
      return onAddCustomField(customFieldToAdd);
    }
    setIsAddingFilter(true);
  };

  const onUpdateCustomFieldFilter = useCallback(
    (id: string, cf: CustomFieldFilter) => {
      onUpdate(filters.map((f) => (f.id === id ? cf : f)));
    },
    [filters, onUpdate],
  );

  const onRemoveCustomFieldFilter = useCallback(
    (id: string) => {
      onUpdate(filters.filter((f) => f.id !== id));
    },
    [filters, onUpdate],
  );

  const renderCustomFieldsFilters = (filtersList: CustomFieldFilter[]) => {
    if (!customFields) return null;

    return filtersList.map((filter) => {
      const availableIds = customFieldsAvailable.map((c) => c.id);
      const availableFields = customFields.filter(
        (c) => availableIds.includes(c.id) || c.id === filter.id,
      );

      switch (filter.type) {
        case CustomFieldDataType.STRING:
        case CustomFieldDataType.NUMBER:
          return (
            <CustomFieldStringNumberFilterComponent
              key={filter.id}
              filter={filter}
              onClear={() => onRemoveCustomFieldFilter(filter.id)}
              onUpdate={(id, cf) => onUpdateCustomFieldFilter(id, cf)}
              availableFields={availableFields}
            />
          );
        case CustomFieldDataType.DATE:
          return (
            <CustomFieldDateFilterComponent
              key={filter.id}
              filter={filter}
              onClear={() => onRemoveCustomFieldFilter(filter.id)}
              onUpdate={(id, cf) => onUpdateCustomFieldFilter(id, cf)}
              availableFields={availableFields}
            />
          );
        case CustomFieldDataType.BOOLEAN:
          return (
            <CustomFieldBooleanFilterComponent
              key={filter.id}
              filter={filter}
              onClear={() => onRemoveCustomFieldFilter(filter.id)}
              onUpdate={(id, cf) => onUpdateCustomFieldFilter(id, cf)}
              availableFields={availableFields}
            />
          );
        case CustomFieldDataType.SELECT:
        case CustomFieldDataType.ARR_STRING:
        case CustomFieldDataType.ARR_NUMBER: {
          const options = customFields.find((c) => c.id === filter.id)?.select_type_options;
          return (
            <CustomFieldSelectListFilterComponent
              key={filter.id}
              filter={filter}
              onClear={() => onRemoveCustomFieldFilter(filter.id)}
              onUpdate={(id, cf) => onUpdateCustomFieldFilter(id, cf)}
              availableFields={availableFields}
              options={options}
            />
          );
        }
        default: {
          const _: never = filter;
          return _;
        }
      }
    });
  };

  if (!customFields) return <Skeleton height={42} />;

  return (
    <Stack gap={3}>
      {renderCustomFieldsFilters(filters)}
      {isAddingFilter && (
        <SimpleDropdownSelect
          opened
          onClose={() => setIsAddingFilter(false)}
          placeholder={formatMessage({ id: 'filter-filterBy' })}
          options={customFieldsAvailable.map((option) => ({
            name: option.label,
            value: option.id,
          }))}
          onChange={(v) => {
            const customField = customFieldsAvailable.find((c) => c.id === v);
            if (!customField) return;
            onAddCustomField(customField);
          }}
        />
      )}
      {customFieldToAdd && !isAddingFilter && (
        <Stack alignSelf="flex-start">
          <SimpleButton startIcon={<PlusIcon />} onClick={handleSelectFilterToAdd}>
            {formatMessage({ id: 'filter-addFilter' })}
          </SimpleButton>
        </Stack>
      )}
    </Stack>
  );
};

const createCustomFieldFilter = (cf: CustomField): CustomFieldFilter => {
  switch (cf.data_type) {
    case CustomFieldDataType.STRING:
      return {
        type: CustomFieldDataType.STRING,
        id: cf.id,
        operator: TextOperator.All,
        value: undefined,
      };
    case CustomFieldDataType.NUMBER:
      return {
        type: CustomFieldDataType.NUMBER,
        id: cf.id,
        operator: TextOperator.All,
        value: undefined,
      };
    case CustomFieldDataType.DATE:
      return {
        type: CustomFieldDataType.DATE,
        id: cf.id,
        operator: DateOperator.All,
        value: undefined,
      };
    case CustomFieldDataType.BOOLEAN:
      return {
        type: CustomFieldDataType.BOOLEAN,
        id: cf.id,
        operator: BooleanOperator.All,
        value: undefined,
      };
    case CustomFieldDataType.SELECT:
      return {
        type: CustomFieldDataType.SELECT,
        id: cf.id,
        operator: SelectListOperator.All,
        value: undefined,
      };
    case CustomFieldDataType.ARR_STRING:
      return {
        type: CustomFieldDataType.ARR_STRING,
        id: cf.id,
        operator: SelectListOperator.All,
        value: undefined,
      };
    case CustomFieldDataType.ARR_NUMBER:
      return {
        type: CustomFieldDataType.ARR_NUMBER,
        id: cf.id,
        operator: SelectListOperator.All,
        value: undefined,
      };
    case CustomFieldDataType.MEDICAL:
      throw new Error('Medical need filter is not supported');
    default: {
      const _: never = cf.data_type;
      return _;
    }
  }
};

const CustomFieldStringNumberFilterComponent: FC<{
  filter: CustomFieldTextNumberFilter;
  onClear: () => void;
  onUpdate: (id: string, filter: CustomFieldFilter) => void;
  availableFields: CustomField[];
}> = ({ filter, onClear, onUpdate, availableFields }) => {
  const { formatMessage } = useIntl();

  const renderFilterValue = () => {
    if (filter.operator !== TextOperator.Contains) return;

    switch (filter.type) {
      case CustomFieldDataType.STRING:
        return (
          <FormTextField
            value={filter.value}
            onChange={(e) => onUpdate(filter.id, { ...filter, value: e.target.value })}
            placeholder={formatMessage({ id: 'filter-Text' })}
          />
        );
      case CustomFieldDataType.NUMBER:
        return (
          <FormTextField
            value={filter.value}
            type="number"
            onChange={(e) => {
              onUpdate(filter.id, { ...filter, value: e.target.value });
            }}
            placeholder={formatMessage({ id: 'number' })}
          />
        );
      default: {
        const _: never = filter.type;
        return _;
      }
    }
  };

  return (
    <CustomFieldFilterContainer onDelete={onClear}>
      <CustomFieldSelect
        selectedCustomFieldId={filter.id}
        options={availableFields}
        onChange={(customField) => onUpdate(filter.id, createCustomFieldFilter(customField))}
      />
      <OperatorSelect
        options={TEXT_OPERATOR_OPTIONS}
        operator={filter.operator}
        onUpdate={(operator) => {
          if (operator === filter.operator) return;

          switch (operator) {
            case TextOperator.All:
            case TextOperator.None:
              onUpdate(filter.id, { ...filter, value: undefined, operator });
              return;
            case TextOperator.Contains:
              onUpdate(filter.id, { ...filter, value: '', operator });
              return;
            default: {
              const _: never = operator;
              return _;
            }
          }
        }}
      />
      {renderFilterValue()}
    </CustomFieldFilterContainer>
  );
};

const CustomFieldBooleanFilterComponent: FC<{
  filter: CustomFieldBooleanFilter;
  onClear: () => void;
  onUpdate: (id: string, filter: CustomFieldFilter) => void;
  availableFields: CustomField[];
}> = ({ filter, onClear, onUpdate, availableFields }) => {
  const { formatMessage } = useIntl();

  return (
    <CustomFieldFilterContainer onDelete={onClear}>
      <CustomFieldSelect
        selectedCustomFieldId={filter.id}
        options={availableFields}
        onChange={(customField) => onUpdate(filter.id, createCustomFieldFilter(customField))}
      />
      <OperatorSelect
        options={BOOLEAN_OPERATOR_OPTIONS}
        operator={filter.operator}
        onUpdate={(operator) => {
          if (operator === filter.operator) return;

          switch (operator) {
            case BooleanOperator.IsEqual:
              onUpdate(filter.id, { ...filter, value: true, operator });
              break;
            case BooleanOperator.All:
            case BooleanOperator.None:
              onUpdate(filter.id, { ...filter, value: undefined, operator });
              break;
            default: {
              const _: never = operator;
              return _;
            }
          }
        }}
      />
      {filter.operator === BooleanOperator.IsEqual && (
        <Stack flexDirection="row" gap={1}>
          <RadioButton
            checked={filter.value === true}
            onChange={() => onUpdate(filter.id, { ...filter, value: true })}
            label={formatMessage({ id: 'yes' })}
          />
          <RadioButton
            checked={filter.value === false}
            onChange={() => onUpdate(filter.id, { ...filter, value: false })}
            label={formatMessage({ id: 'no' })}
          />
        </Stack>
      )}
    </CustomFieldFilterContainer>
  );
};

const CustomFieldSelectListFilterComponent: FC<{
  options?: CustomFieldSelectOption[];
  filter: CustomFieldSelectListFilter;
  onClear: () => void;
  onUpdate: (id: string, filter: CustomFieldFilter) => void;
  availableFields: CustomField[];
}> = ({ filter, onClear, onUpdate, availableFields, options = [] }) => {
  const { formatMessage } = useIntl();

  return (
    <CustomFieldFilterContainer onDelete={onClear}>
      <CustomFieldSelect
        selectedCustomFieldId={filter.id}
        options={availableFields}
        onChange={(customField) => onUpdate(filter.id, createCustomFieldFilter(customField))}
      />
      <OperatorSelect
        options={SELECT_LIST_OPERATOR_OPTIONS}
        operator={filter.operator}
        onUpdate={(operator) => {
          if (operator === filter.operator) return;

          switch (operator) {
            case SelectListOperator.IsOneOf:
              onUpdate(filter.id, { ...filter, value: [], operator });
              break;
            case SelectListOperator.All:
            case SelectListOperator.None:
              onUpdate(filter.id, { ...filter, value: undefined, operator });
              break;
            default: {
              const _: never = operator;
              return _;
            }
          }
        }}
      />
      {filter.operator === SelectListOperator.IsOneOf && (
        <DropdownSelectFilter
          placeholder={formatMessage({ id: 'select-option' })}
          options={options.map((o) => o.label)}
          getLabel={(v) => v}
          selected={filter.value}
          onSelect={(v) =>
            onUpdate(filter.id, { ...filter, value: toggleArrayProperty(filter.value, v) })
          }
        />
      )}
    </CustomFieldFilterContainer>
  );
};

const CustomFieldDateFilterComponent: FC<{
  filter: CustomFieldDateFilter;
  onClear: () => void;
  onUpdate: (id: string, filter: CustomFieldFilter) => void;
  availableFields: CustomField[];
}> = ({ filter, onClear, onUpdate, availableFields }) => {
  const { formatMessage } = useIntl();

  const renderFilterValue = () => {
    switch (filter.operator) {
      case DateOperator.InRange:
        return (
          <DateRangeSelect
            date={filter.value}
            onSetDate={([fromDate, toDate]) =>
              onUpdate(filter.id, {
                ...filter,
                value: [
                  format(fromDate, DEFAULT_DATE_FORMAT_FNS),
                  format(toDate, DEFAULT_DATE_FORMAT_FNS),
                ],
              })
            }
            placeholder={formatMessage({ id: 'assessments-Date' })}
          />
        );
      case DateOperator.IsEqual:
        return (
          <DateSelect
            onSetDate={(d) =>
              onUpdate(filter.id, {
                ...filter,
                value: [format(d, DEFAULT_DATE_FORMAT_FNS)],
              })
            }
            date={filter.value[0]}
            placeholder={formatMessage({ id: 'assessments-Date' })}
          />
        );
      case DateOperator.All:
      case DateOperator.None:
        return null;
      default: {
        const _: never = filter;
        return _;
      }
    }
  };

  return (
    <CustomFieldFilterContainer onDelete={onClear}>
      <CustomFieldSelect
        selectedCustomFieldId={filter.id}
        options={availableFields}
        onChange={(customField) => onUpdate(filter.id, createCustomFieldFilter(customField))}
      />
      <OperatorSelect
        options={DATE_OPERATOR_OPTIONS}
        operator={filter.operator}
        onUpdate={(operator) => {
          if (operator === filter.operator) return;

          const today = format(new Date(), DEFAULT_DATE_FORMAT_FNS);

          switch (operator) {
            case DateOperator.InRange:
              onUpdate(filter.id, { ...filter, value: [today, today], operator });
              break;
            case DateOperator.IsEqual:
              onUpdate(filter.id, { ...filter, value: [today], operator });
              break;
            case DateOperator.All:
            case DateOperator.None:
              onUpdate(filter.id, { ...filter, value: undefined, operator });
              break;
            default: {
              const _: never = operator;
              return _;
            }
          }
        }}
      />
      {renderFilterValue()}
    </CustomFieldFilterContainer>
  );
};

const RadioButton: FC<{ checked: boolean; onChange: () => void; label: string }> = ({
  checked,
  onChange,
  label,
}) => {
  return (
    <Stack
      sx={(theme) => ({
        border: theme.mixins.borderControlValue(),
        borderRadius: theme.spacing(1.25),
        px: 2,
        py: 1.25,
        cursor: 'pointer',
      })}
      onClick={onChange}
    >
      <FormRadioStyled label={label} control={<Radio checked={checked} />} />
    </Stack>
  );
};

const CustomFieldFilterContainer: FC<PropsWithChildren<{ onDelete: () => void }>> = ({
  onDelete,
  children,
}) => {
  return (
    <Stack flexDirection="row" gap={2} alignItems="flex-start">
      <Stack flex={1} gap={1}>
        {children}
      </Stack>
      <Stack pt={1.25}>
        <IconButton inverse onClick={onDelete}>
          <DeleteIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
};
